import { makeStyles } from '@material-ui/styles'

import { useAppSelector } from 'app/store'
import PollingData from 'core/components/PollingData'
import { ViewMode } from 'core/components/ViewModeToggle'
import Progress from 'core/components/progress/Progress'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import RadioDropdownFilter from '../../../networks/dns/records/RadioDropdownFilter'
import { IAppsDetailsPageTabs } from '../../model'
import { listTemplates } from '../../repositories/actions'
import { templateSelector } from '../../repositories/selector'
import AppHeaderPrimaryActions from '../AppHeaderPrimaryActions'
import CommonActionPopover from '../CommonActionPopover'
import { filterAppsByOwnerAndVersion, getOwnerList, getVersionList } from '../helper'
import CreateTemplate from './CreateTemplate'
import DeleteTemplate from './DeleteTemplate'
import TemplateCardView from './TemplateCardView'
import TemplateListView from './TemplateListView'
import { gridContainer } from '../commonStyles'

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    paddingBottom: theme.spacing(1),
  },
  gridContainer: gridContainer(theme),
  gridItem: {
    width: '100%',
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    '& .inputFrame': {
      width: 110,
      [theme.breakpoints.down(1400)]: {
        width: 80,
      },
      [theme.breakpoints.up(1500)]: {
        width: 150,
      },
    },
  },
  appInfoFooterTitle: {
    color: theme.components.typography.passive,
    textTransform: 'uppercase',
  },
  divider: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  sharedContainer: {
    marginTop: theme.spacing(3),
  },
  progressOverlay: {
    maxHeight: '50%',
    boxSizing: 'border-box',
  },
  emptyContent: {
    paddingTop: '10%',
  },
}))

const Templates = () => {
  const [view, setView] = useState<ViewMode>(ViewMode.CARD)
  const classes = useStyles()
  const oneSecond = 1000
  const { loading, reload } = useListAction(listTemplates)
  const appDetails = useAppSelector(templateSelector)

  const [filteredData, setFilteredData] = useState(appDetails || [])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState([])
  const [selectedVersion, setSelectedVersion] = useState([])
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const filteredApps = filterAppsByOwnerAndVersion(filteredData, selectedOwner, selectedVersion)
  const [selectedTemplate, setSelectedTemplate] = useState(null)

  const handlePopoverClick = useCallback((event, item) => {
    setAnchorEl(event.currentTarget)
    setSelectedTemplate(item) // set the selected card's data
    setOpen(true)
  }, [])

  const handleSearch = useCallback(
    (filteredResults) => {
      setFilteredData(filteredResults?.appDetails)
    },
    [appDetails],
  )
  const handleApplyOwnerFilter = ({ selectedItems }) => {
    setSelectedOwner(Array.isArray(selectedItems) ? selectedItems : [selectedItems])
  }
  const handleApplyVersionFilter = ({ selectedItems }) => {
    setSelectedVersion(Array.isArray(selectedItems) ? selectedItems : [selectedItems])
  }
  const handleReload = useCallback(async (refetch = false, updateLoadingState = true) => {
    reload(refetch, updateLoadingState)
  }, [])
  useEffect(() => {
    setFilteredData(appDetails)
  }, [appDetails])

  return (
    <>
      <PollingData hidden loading={loading} onReload={reload} refreshDuration={oneSecond * 30} />
      <CreateTemplate
        addRoute={routeManager.routes.openstack.createTemplate}
        tabName={IAppsDetailsPageTabs.Templates}
      />
      {showDeleteDialog && selectedTemplate && (
        <DeleteTemplate
          rows={[selectedTemplate]}
          onClose={() => {
            setShowDeleteDialog(false)
            setSelectedTemplate(null)
          }}
        />
      )}
      <AppHeaderPrimaryActions
        addUrl={routeManager.routes.openstack.createTemplate.path({
          tab: IAppsDetailsPageTabs.Templates,
        })}
        addText="Create Template"
        view={view}
        setView={setView}
        handleSearch={handleSearch}
        data={{ appDetails }}
        searchKeys={['headerName', 'ownerName']}
        onReload={handleReload}
        HeaderActions={
          <div className={classes.filtersContainer}>
            <RadioDropdownFilter
              label="Owner"
              items={getOwnerList(appDetails)}
              loading={false}
              handleApplyFilter={(selectedItems) => handleApplyOwnerFilter({ selectedItems })}
            />
            <RadioDropdownFilter
              label="Branch"
              placeholder="All Branches"
              items={getVersionList(appDetails)}
              handleApplyFilter={(selectedItems) => handleApplyVersionFilter({ selectedItems })}
            />
          </div>
        }
      >
        <Progress
          loading={loading}
          message={'Loading Templates...'}
          // renderContentOnMount
          overlay
          className={classes.progressOverlay}
        >
          {filteredApps && filteredApps?.length > 0 ? (
            view === ViewMode.LIST ? (
              <>
                {filteredApps?.map((item) => (
                  <div className={classes.grid} key={item.uid}>
                    <TemplateListView
                      iconName={item?.iconName}
                      headerName={item?.headerName}
                      status={item?.status}
                      vms={item?.vms}
                      version={item?.version}
                      subHeaderName={item?.subHeaderName}
                      ownerName={item?.ownerName}
                      handleClick={(e) => handlePopoverClick(e, item)}
                      data={item}
                    />
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className={classes.gridContainer}>
                  {filteredApps?.map((item) => (
                    <div key={item.uid} className={classes.gridItem}>
                      <TemplateCardView
                        iconName={item?.iconName}
                        headerName={item?.headerName}
                        subHeaderName={item?.subHeaderName}
                        status={item?.status}
                        vms={item?.vms}
                        version={item?.version}
                        ownerName={item?.ownerName}
                        handleClick={(e) => handlePopoverClick(e, item)}
                        data={item}
                      />
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            <div className={classes.emptyContent}>
              <EmptyContent
                title={'No Template Found'}
                subTitle={'Create one in order to see details'}
              />
            </div>
          )}
        </Progress>
      </AppHeaderPrimaryActions>

      <CommonActionPopover
        open={open}
        setOpen={setOpen}
        anchorEl={anchorEl}
        actions={[
          {
            label: 'Delete',
            icon: 'trash-can',
            onClick: () => {
              setShowDeleteDialog(true)
              setOpen(false)
            },
            disabled: selectedTemplate?.status === 'deleting' ? true : false,
          },
        ]}
      />
    </>
  )
}

export default Templates
